import { ThemeProvider } from "@emotion/react";
import styled from "@emotion/styled";
import { FormApi } from "final-form";
import createDecorator from "final-form-focus";
import { first, omit } from "lodash";
import * as React from "react";
import { Component, useCallback, useMemo } from "react";
import { Field, FieldInputProps, Form } from "react-final-form";
import { RouteComponentProps, withRouter } from "react-router-dom";

import {
  DesignToken,
  FormField,
  IPickerListItem,
  Input,
  ModalPortal,
  Picker,
  RadiusToken,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import { DeleteTextButton } from "components/button/DeleteTextButton";
import {
  CheckboxWithLabelDescription,
  CheckboxWithLabelFinalForm,
  FixedSaveBar,
  FloatedInput,
  SaveBarMode,
} from "components/form";
import { BackButton, Grid, ViewContainer, ViewStack } from "components/grid";
import Block from "components/layout/Block";
import { StyledLink } from "components/links/StyledLink";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import { config } from "config/config";
import { ResponsiveBreakpoint, ResponsiveBreakpointType } from "constants/responsive-breakpoint";
import { agentAcceptsAutoassignmentFromTeam } from "helpers/agent";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { setDocumentTitle } from "helpers/routing";
import { SettingsRoutes } from "routes/settings/constants";
import { IAgent } from "store/entities/agents";
import { IContactForm } from "store/entities/contact-forms/interfaces";
import { IMailbox } from "store/entities/mailboxes/interfaces";
import { IReplyAddress } from "store/entities/reply-addresses/interfaces";
import { ITeam, UpdateTeamForm } from "store/entities/teams";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";
import { legacyTheme } from "themes/legacyTheme";
import { compose, maxLength, required } from "utils/validators";

import { Members } from "./Members";

export interface ITeamFormProps extends RouteComponentProps {
  team: ITeam | null;
  agents: IAgent[];
  defaultTeamName: string;
  mailboxes: IMailbox[];
  contactForms: Record<string, IContactForm>;
  replyAddresses: IReplyAddress[];
  licenseID: number;
  onCancel(): void;
  onSubmit(team: UpdateTeamForm): void;
  onDelete(teamId: string): void;
  onGoToMailboxes: () => void;
  onGoToContactForms: () => void;
}

interface IState {
  isOpenDeleteModal: boolean;
  isOpenDeleteWarningModal: boolean;
}

interface IReplyAddressSelectProps {
  value: string | null;
  replyAddresses: IReplyAddress[];
  licenseID: number;
  onChange: FieldInputProps<string, HTMLElement>["input"]["onChange"];
}

const ReplyAddressSelect: React.FC<IReplyAddressSelectProps> = ({ value, replyAddresses, licenseID, onChange }) => {
  const options = useMemo(
    () => [
      ...replyAddresses.map(address => ({ key: address.id, name: address.email })),
      { key: "", name: `${licenseID}@${config.defaultMailboxSuffix}` },
    ],
    [licenseID, replyAddresses]
  );

  const handleSelect = useCallback(
    (selectedItems: IPickerListItem[] | null) => {
      const selectedOption = first(selectedItems);

      if (selectedOption) {
        onChange({ target: { value: selectedOption.key } });
      }
    },
    [onChange]
  );

  return (
    <FormField labelText="Reply address" css={{ width: "100%" }}>
      <Picker
        id="reply-address-id"
        options={options}
        onSelect={handleSelect}
        selected={options.filter(option => option.key === value)}
        hideClearButton
      />
    </FormField>
  );
};

class TeamForm extends Component<ITeamFormProps, IState> {
  state = {
    isOpenDeleteModal: false,
    isOpenDeleteWarningModal: false,
  };

  private focusOnError = createDecorator();

  componentDidMount(): void {
    const { team } = this.props;
    if (team) {
      setDocumentTitle(`Edit team: ${team.name}`);
    } else {
      setDocumentTitle("New team");
    }
  }

  onDelete = (): void => {
    const { onDelete, team } = this.props;
    if (team) {
      onDelete(team.id);
    }
  };

  handleDeleteTeam = (): void => {
    if (this.isTeamUsedInMailboxes() || this.isTeamUsedInContactForms()) {
      this.toggleDeleteWarningModal();
    } else {
      this.toggleDeleteModal();
    }
  };

  handleSubmit = (values: UpdateTeamForm, form: FormApi<UpdateTeamForm>): void => {
    const changedFields = Object.entries(form.getState().dirtyFields)
      .filter(([_, value]) => value)
      .map(([key]) => key);

    trackEngagement("Teams: Team updated", EventPlace.Teams, {
      changedFields,
    });

    const changedAutoassignment = form.getState().dirtyFields.autoassignmentAgentIDs;
    if (changedAutoassignment) {
      this.props.onSubmit(values);
    } else {
      this.props.onSubmit(omit(values, ["autoassignmentAgentIDs"]));
    }
  };

  toggleDeleteModal = (): void => {
    this.setState(prevState => ({
      isOpenDeleteModal: !prevState.isOpenDeleteModal,
    }));
  };

  toggleDeleteWarningModal = (): void => {
    this.setState(prevState => ({
      isOpenDeleteWarningModal: !prevState.isOpenDeleteWarningModal,
    }));
  };

  getInitialValues = (team: Partial<ITeam> | null): UpdateTeamForm => {
    const { agents } = this.props;
    if (!team) {
      return {
        name: "",
        agentIds: [],
        replyName: null,
        replyAddressID: null,
        autoassignmentAgentIDs: [],
      };
    }

    const autoassignmentAgentIDs = agents
      .filter(agent => agentAcceptsAutoassignmentFromTeam(agent, team.id))
      .map(agent => agent.id);

    return {
      ...team,
      autoassignmentAgentIDs,
    };
  };

  isTeamUsedInMailboxes = (): boolean =>
    this.props.mailboxes.some(mailbox => this.props.team && mailbox.teamId === this.props.team.id);
  isTeamUsedInContactForms = (): boolean =>
    Object.values(this.props.contactForms).some(form => this.props.team && form.teamID === this.props.team.id);

  render(): React.ReactNode {
    const { agents, team, defaultTeamName, onCancel, onGoToMailboxes, onGoToContactForms, replyAddresses, licenseID } =
      this.props;
    const { isOpenDeleteModal, isOpenDeleteWarningModal } = this.state;
    const backButton = <BackButton onClick={onCancel}>Back</BackButton>;

    const deleteWarningReason = this.isTeamUsedInMailboxes() ? "forwarding addresses" : "contact forms";

    return (
      <ThemeProvider theme={legacyTheme}>
        <Grid relativeWidths={[3, 1]} minWidths={[320, 320]} focusedColumn={0}>
          <ViewStack>
            <ViewContainer title={team ? "Edit team" : "New team"} placement="main-content" leftNodes={backButton}>
              <Block>
                <Form
                  decorators={[this.focusOnError]}
                  onSubmit={this.handleSubmit}
                  initialValues={this.getInitialValues(team)}
                  keepDirtyOnReinitialize
                  render={({ handleSubmit, pristine, invalid }): React.ReactNode => (
                    <Overflow>
                      <Page>
                        <form onSubmit={handleSubmit}>
                          <FloatingInputsWrapper>
                            <Field<ITeam["name"]>
                              validate={compose(
                                required("Name is required"),
                                maxLength("Name is too long (180 characters)", 180)
                              )}
                              // @ts-expect-error TS(2322)
                              component={FloatedInput}
                              name="name"
                              label="Name"
                              size="big"
                            />
                          </FloatingInputsWrapper>

                          <Header>Agents</Header>
                          <Field<string[]>
                            name="agentIds"
                            validate={required("Please choose at least one agent")}
                            render={({ input, meta }): React.ReactNode => (
                              <Members
                                agents={agents}
                                team={team}
                                value={input.value}
                                onChange={input.onChange}
                                validationMessage={meta.submitFailed && meta.invalid ? meta.error : ""}
                              />
                            )}
                          />

                          <Header>Reply address</Header>
                          <Block marginV="1em">
                            <Text>
                              Choose the display name and the reply address for this team. It will be visible to your
                              customers. <br />
                              To manage the reply addresses, go to{" "}
                              <StyledLink to={SettingsRoutes.ReplyAddresses}>Settings</StyledLink>.
                            </Text>
                          </Block>
                          <Block maxWidth="400px">
                            <Field<ITeam["replyName"]>
                              name="replyName"
                              validate={value => (value === null ? "" : required("Display name can't be blank")(value))}
                              labelText="Display name"
                              render={({ input, meta }): React.ReactNode => (
                                <FormField
                                  labelText="Display name"
                                  error={meta.submitFailed && meta.invalid ? meta.error : undefined}
                                  css={{ width: "100%" }}
                                >
                                  <Input
                                    id="reply-name"
                                    error={meta.submitFailed && meta.invalid ? meta.error : undefined}
                                    placeholder="Set a display name, e.g., company name"
                                    {...input}
                                    value={input.value || ""}
                                  />
                                </FormField>
                              )}
                            />
                            <Field<ITeam["replyAddressID"]>
                              name="replyAddressID"
                              render={({ input }): React.ReactNode => (
                                <ReplyAddressSelect
                                  value={input.value}
                                  onChange={input.onChange}
                                  replyAddresses={replyAddresses}
                                  licenseID={licenseID}
                                />
                              )}
                            />
                          </Block>
                          {team && (
                            <>
                              <Header>Default team</Header>
                              <Block>
                                <Field
                                  // @ts-expect-error TS(2322)
                                  component={CheckboxWithLabelFinalForm}
                                  type="checkbox"
                                  name="isDefault"
                                  disabled={team.isDefault}
                                >
                                  <Text>Set as default team</Text>
                                </Field>
                                <CheckboxWithLabelDescription>
                                  <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>
                                    {team.isDefault && (
                                      <>
                                        Current default team: <strong>{defaultTeamName}</strong>. Edit other team to
                                        change it.
                                      </>
                                    )}
                                    {!team.isDefault && (
                                      <>
                                        Current default team: <strong>{defaultTeamName}</strong>.<br />
                                        Selecting the checkbox will change the default team to{" "}
                                        <strong>{team.name}</strong>.
                                      </>
                                    )}
                                  </Text>
                                </CheckboxWithLabelDescription>
                              </Block>
                              <Header>Delete team</Header>
                              <Block className="delete-team">
                                {team.isDefault && (
                                  <Text>
                                    Default team cannot be deleted. If you want to delete this team, mark other team as
                                    default first.
                                  </Text>
                                )}
                                {!team.isDefault && (
                                  <Text>
                                    You will delete this team, related statistics, canned responses and tags.
                                    <br />
                                    Tickets will be assigned to the default team.
                                    <br />
                                    <DeleteTextButton type="button" onClick={this.handleDeleteTeam}>
                                      Delete team.
                                    </DeleteTextButton>
                                  </Text>
                                )}
                              </Block>
                            </>
                          )}
                        </form>
                      </Page>

                      <FixedSaveBar
                        mode={pristine ? SaveBarMode.Hidden : SaveBarMode.OfferSave}
                        saveDisabled={pristine || invalid}
                        onSave={handleSubmit}
                        onCancel={onCancel}
                      />
                    </Overflow>
                  )}
                />
              </Block>

              {isOpenDeleteModal && (
                <ModalPortal zIndex={StackingContextLevel.Modal}>
                  <ConfirmActionModal
                    heading={`Delete ${this.props.team ? this.props.team.name : "a team"}`}
                    onClose={this.toggleDeleteModal}
                    primaryAction={{
                      label: "Delete",
                      actionType: "destructive",
                      onClick: this.onDelete,
                      dataTestId: "delete-modal-button",
                    }}
                    secondaryAction={{
                      label: "Cancel",
                      onClick: this.toggleDeleteModal,
                    }}
                  >
                    You will delete this team, related statistics, canned responses and tags. Tickets will be assigned
                    to the default team.
                  </ConfirmActionModal>
                </ModalPortal>
              )}

              {isOpenDeleteWarningModal && (
                <ModalPortal zIndex={StackingContextLevel.Modal}>
                  <ConfirmActionModal
                    heading="Can't delete this team"
                    onClose={this.toggleDeleteWarningModal}
                    primaryAction={{
                      label: "Go to Settings",
                      actionType: "primary",
                      onClick: this.isTeamUsedInMailboxes() ? onGoToMailboxes : onGoToContactForms,
                    }}
                    secondaryAction={{
                      label: "Cancel",
                      onClick: this.toggleDeleteWarningModal,
                    }}
                  >
                    <Text>
                      There are {deleteWarningReason} assigned to the team you want to delete.{" "}
                      {deleteWarningReason === "contact forms" && (
                        <Text>
                          Go to <StyledLink to={SettingsRoutes.ContactForms}>settings</StyledLink> and un-assign the
                          contact form from the team.
                        </Text>
                      )}
                      {deleteWarningReason === "forwarding addresses" && <>Please detach forwarding addresses first.</>}
                    </Text>
                  </ConfirmActionModal>
                </ModalPortal>
              )}
            </ViewContainer>
          </ViewStack>
        </Grid>
      </ThemeProvider>
    );
  }
}

const Overflow = styled.div`
  overflow: auto;
  width: 100%;
  height: 100%;
  background-color: ${withVar(DesignToken.SurfaceSecondaryDefault)};
`;

const Page = styled.div`
  position: relative;
  padding: 68px;
  max-width: 720px;
  margin: 100px auto;
  background: ${withVar(DesignToken.SurfacePrimaryDefault)};
  border-radius: ${withVar(RadiusToken.Radius3)};
  box-sizing: border-box;
  border: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
  @media (max-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile]}) {
    margin: 0;
    padding: ${withVar(SpacingToken.Spacing5)};
  }
`;

const FloatingInputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Header = styled.h2`
  color: ${withVar(DesignToken.ContentBasicPrimary)};
  box-sizing: border-box;
  width: 100%;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
  padding: 11px 20px;
  padding-left: 0;
  margin-bottom: ${withVar(SpacingToken.Spacing5)};
  margin-top: ${withVar(SpacingToken.Spacing10)};
`;

export default withRouter(TeamForm);
